import React, { useState, useEffect } from 'react';
import { Play } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const OurWork = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data.slice(0, 3));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching videos:', error);
      setLoading(false);
    }
  };

  const getVideoEmbedUrl = (url) => {
    const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/;
    const match = url.match(youtubeRegex);
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    return url;
  };

  const isYouTubeUrl = (url) => {
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  if (loading) {
    return (
      <section id="our-work" className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-500 uppercase tracking-widest text-sm">Loading...</p>
          </div>
        </div>
      </section>
    );
  }

  if (videos.length === 0) return null;

  return (
    <section id="our-work" className="py-24 bg-[#050505] relative overflow-hidden" data-testid="our-work-section">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-red-600 mb-4" data-testid="our-work-label">See The Proof</p>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-heading tracking-tight uppercase mb-4" data-testid="our-work-heading">
            <span className="text-white">Our </span>
            <span className="text-gradient-noir">Best Work</span>
          </h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            Cinematic trailers that make readers hit "Buy Now"
          </p>
        </div>

        {/* Videos Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {videos.map((video, index) => (
            <div
              key={video.id || index}
              className="group relative overflow-hidden bg-[#0A0A0A] border border-white/10 hover:border-red-600/50 transition-all duration-300"
              data-testid={`video-card-${index}`}
            >
              {/* Video Embed */}
              <div className="relative aspect-video bg-black">
                {isYouTubeUrl(video.url) ? (
                  <iframe
                    src={getVideoEmbedUrl(video.url)}
                    title={video.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="relative w-full h-full">
                    <video
                      src={video.url}
                      controls
                      className="w-full h-full object-cover"
                      preload="metadata"
                    >
                      Your browser does not support the video tag.
                    </video>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/20 transition-colors duration-300 pointer-events-none">
                      <div className="w-14 h-14 bg-red-700/90 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Play className="text-white ml-1" size={24} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Video Info */}
              <div className="p-5">
                {video.projectType && (
                  <span className="inline-block bg-red-900/30 text-red-500 text-xs font-semibold uppercase tracking-wider px-3 py-1 mb-3" data-testid={`video-type-${index}`}>
                    {video.projectType}
                  </span>
                )}
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-red-500 transition-colors duration-200" data-testid={`video-title-${index}`}>
                  {video.title}
                </h3>
                {video.description && (
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {video.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurWork;
